'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import { CostSummaryCard } from './CostSummaryCard';
import { CostAndFeesSpokeContent } from './CostAndFeesSpoke';
import { calculateEstimateAmounts } from './feeCalculation';
import { getEffectiveCostsAndFees } from './costSelection';
import { useCostAndFeesCalculatorState } from './useCostAndFeesCalculatorState';
import { inlineDetailBody } from './spokes/costAndFeesSpoke.styles';
import { getEmbeddedV3Costs } from '@/src/lib/utils/v3ListingDetail';

type Props = {
  listing: PropertyDetailListing;
};

export const CostAndFeesModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const costsData = getEmbeddedV3Costs(listing);
  const calculatorState = useCostAndFeesCalculatorState();
  const { selectedBedCount, selectedUnitZpid, selectedFeeOptions } = calculatorState;
  const { effectiveCosts, effectiveFees } = useMemo(() => {
    if (!costsData) return { effectiveCosts: null, effectiveFees: null };
    return getEffectiveCostsAndFees(costsData, { selectedBedCount, selectedUnitZpid });
  }, [costsData, selectedBedCount, selectedUnitZpid]);
  const estimateAmounts = calculateEstimateAmounts(effectiveCosts, effectiveFees, selectedFeeOptions);

  const hasCosts = Boolean(costsData?.costs?.monthly?.amount || costsData?.costs?.moveIn?.amount);
  const hasLeaseTerms = Boolean(listing.details?.leaseTerms);

  if (!hasCosts && !hasLeaseTerms) return null;

  const preview = hasCosts && effectiveCosts && (
    <CostSummaryCard
      costs={{ monthly: { amount: estimateAmounts.monthly }, moveIn: { amount: estimateAmounts.moveIn } }}
    />
  );

  return (
    <ShowMoreContent
      controlsId="cost-and-fees-module-details"
      buttonTestId="button-show-more-costs-fees-and-lease"
      preview={preview}
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${t('costAndFeesTitle')}`}
      showLessAriaLabel={`${t('showLess')}: ${t('costAndFeesTitle')}`}
    >
      <CostAndFeesSpokeContent
        listing={listing}
        showHeader={false}
        bodyClassName={inlineDetailBody}
        showEstimateSpecialOffer={false}
        showEstimateSummary={false}
        calculatorState={calculatorState}
      />
    </ShowMoreContent>
  );
};
